import api from './api'
import axios from 'axios'

const API_URL =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_URL_DEV
    : process.env.REACT_APP_API_URL

interface ProcessContractData {
  password: string
  user_id: string
}

export async function processContract(
  contract_id: string,
  data: ProcessContractData
) {
  const response = await api.post(`/contracts/${contract_id}/process`, data)
  return response.data
}

export interface UauSyncLog {
  id: string
  contract_id: string
  status: 'pending' | 'in_progress' | 'success' | 'error'
  request_xml?: string
  response_data?: string
  error_message?: string
  uau_venda_numero?: string
  created_at: string
  updated_at: string
  completed_at?: string
}

export async function syncContractToUau(
  contract_id: string,
  token: string,
  userContextEncoded?: string
): Promise<{ syncLogId: string }> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.post(
    `${API_URL}/uau/sync/${contract_id}`,
    {},
    { headers }
  )
  return response.data
}

export async function getUauSyncStatus(
  contract_id: string,
  token: string,
  userContextEncoded?: string
): Promise<UauSyncLog | null> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(
    `${API_URL}/uau/sync/${contract_id}/status`,
    { headers }
  )
  return response.data
}

export async function getUauSyncLogs(
  contract_id: string,
  token: string,
  userContextEncoded?: string
): Promise<UauSyncLog[]> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(`${API_URL}/uau/sync/${contract_id}/logs`, {
    headers
  })
  return response.data
}

export interface UauAvailability {
  enabled: boolean
  configured: boolean
  missingConfig?: string[]
}

export async function checkUauAvailability(
  contract_id: string,
  token: string,
  userContextEncoded?: string
): Promise<UauAvailability> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.get(
    `${API_URL}/uau/sync/${contract_id}/availability`,
    { headers }
  )
  return response.data
}

export async function uploadContractFile(
  contract_id: string,
  file: File,
  token: string,
  userContextEncoded?: string
): Promise<{ success: boolean; url: string; message: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const headers: Record<string, string> = {
    'Content-Type': 'multipart/form-data',
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  const response = await axios.post(
    `${API_URL}/contracts/${contract_id}/upload-file`,
    formData,
    { headers }
  )
  return response.data
}

export interface UauSeller {
  codigoVendedor?: number
  nomeVendedor?: string
  // Alternative field names from UAU API
  codigo?: number
  nome?: string
  cpf?: string
}

export async function getUauSellers(
  token: string,
  userContextEncoded?: string
): Promise<UauSeller[]> {
  console.log('getUauSellers SERVICE - start')
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }

  console.log('getUauSellers SERVICE - calling:', `${API_URL}/uau/sellers`)
  try {
    const response = await axios.get(`${API_URL}/uau/sellers`, {
      headers,
      timeout: 15000 // 15 second timeout
    })
    console.log(
      'getUauSellers SERVICE - response:',
      response.status,
      response.data
    )
    return response.data
  } catch (error: any) {
    console.log(
      'getUauSellers SERVICE - error:',
      error.response?.status,
      error.response?.data,
      error.message
    )
    throw error
  }
}

export async function updateBillingPerson(
  contractId: string,
  billingPersonId: string,
  token: string,
  userContextEncoded?: string
): Promise<{ billing_person_id: string }> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }
  const response = await axios.patch(
    `${API_URL}/contracts/${contractId}/billing-person`,
    { billing_person_id: billingPersonId },
    { headers }
  )
  return response.data
}

export interface SyncUauInstallmentsResult {
  contractId: string
  codVenda: string
  empresa: string
  obra: string
  toCancel: Array<{
    billingId: string
    installment_number: number
    tipoParcela: string
    parcelaGeral: number
    value: number
    due_date: string
  }>
  toCreate: Array<{
    installment_number: number
    tipoParcela: string
    parcelaGeral: number
    value: number
    due_date: string
    paymentPartName: string
    isNewPaymentPart: boolean
  }>
  skippedPaidInPropToken: number
  skippedBolecodeEmitted: number
  skippedTipoNotTracked: number
  unchanged: number
  errors: Array<{ kind: 'cancel' | 'create'; ref: string; message: string }>
  dryRun: boolean
}

export async function syncContractInstallmentsWithUau(
  contractId: string,
  dryRun: boolean,
  token: string,
  userContextEncoded?: string
): Promise<SyncUauInstallmentsResult> {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }
  const response = await axios.post(
    `${API_URL}/contracts/${contractId}/sync-uau-installments`,
    { dryRun },
    { headers }
  )
  return response.data
}

export const previewMinuta = async (
  draft: any,
  token?: string,
  context?: string
) => {
  const { data } = await api.post('/contracts/preview-minuta', draft, {
    headers: {
      Authorization: `bearer ${token}`,
      'X-User-Context': context
    }
  })
  return data
}

export const getEditedMinuta = async (
  contractId: string,
  token?: string,
  context?: string
): Promise<string | null> => {
  const { data } = await api.get(`/contracts/${contractId}/edited-minuta`, {
    headers: {
      Authorization: `bearer ${token}`,
      'X-User-Context': context
    }
  })
  return data?.edited_minuta_html ?? null
}
